<script lang="ts">
	import { onMount } from 'svelte'
	import { tick } from 'svelte'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { Icon } from '@repo/commons/components'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface Props {
		label?: string
		isFullWidth?: boolean
		tooltip?: TooltipProps
		/**
		 * The unified value:
		 * - In continuous mode, a number between 0 and 100.
		 * - In enum mode, one of the strings from enumValues.
		 */
		value?: number | string
		/**
		 * Optional array of enum values. If provided (and non-empty),
		 * the slider operates in discrete enum mode.
		 */
		enumValues?: string[] | undefined
		disabled?: boolean
		onvalueChange?: ((value: string) => void) | null
	}

	let {
		label = 'Label',
		tooltip,
		isFullWidth = false,
		value = $bindable(0),
		enumValues = undefined,
		disabled = false,
		onvalueChange = null
	}: Props = $props()

	let sliderValue: number = $state(0)

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	// Initialize sliderValue only once on mount.
	onMount(() => {
		if (isDiscrete) {
			const idx = enumValues!.indexOf(value as string)
			sliderValue = idx === -1 ? 0 : idx * step
		} else {
			sliderValue = typeof value === 'number' ? value : 0
		}
	})

	async function updateValue(newValue: number) {
		await tick()
		if (isDiscrete) {
			const discreteIndex = Math.round(newValue / step)
			const clamped = discreteIndex * step
			if (sliderValue !== clamped) {
				sliderValue = clamped
			}
			const newEnumValue = enumValues![discreteIndex]
			if (newEnumValue !== value) {
				value = newEnumValue
				onvalueChange?.(newEnumValue)
			}
		} else {
			if (newValue !== value) {
				value = newValue
				onvalueChange?.(newValue)
			}
		}
	}
	const isDiscrete = $derived(enumValues && enumValues.length > 0)
	// In discrete mode, compute the step (e.g. 6 values means steps of 100/5 = 20).
	const step = $derived(
		isDiscrete ? (enumValues!.length > 1 ? 100 / (enumValues!.length - 1) : 100) : 0.1
	)
	// What to show on the right (either a percentage or the enum label). Let's ensure to capitalize
	// the first letter of the enum values as well
	const displayedValue = $derived(
		isDiscrete
			? (() => {
					const enumVal = enumValues![Math.round(sliderValue / step)]
					return enumVal.charAt(0).toUpperCase() + enumVal.slice(1)
				})()
			: `${sliderValue.toFixed(1)}%`
	)
	// Compute the background style in TS rather than using a CSS variable.
	const backgroundStyle = $derived(
		`linear-gradient(to right, #007acc ${sliderValue}%, #e0e0e0 ${sliderValue}%)`
	)
	$effect(() => {
		updateValue(sliderValue)
	})
</script>

<div class="slider-container" class:fullWidth={isFullWidth}>
	<div class="slider-info">
		<div class="slider-label">
			{label}
			{#if tooltip?.text || tooltip?.html}
				{#await import('@repo/commons/components') then { Tooltip }}
					<Tooltip {...tooltipProps}>
						<Icon name="info" size="sm" class="text-brand" />
					</Tooltip>
				{/await}
			{/if}
		</div>
		<div class="slider-percentage">{displayedValue}</div>
	</div>
	<div class="slider-wrapper">
		<input
			type="range"
			min="0"
			max="100"
			{step}
			bind:value={sliderValue}
			{disabled}
			class="slider"
			style="background: {backgroundStyle};"
		/>
	</div>
</div>

<style>
	.slider-container {
		@apply w-[300px] h-12 flex flex-col justify-center items-start gap-2;
	}

	.slider-info {
		@apply w-full flex justify-between items-center;
	}

	.slider-label {
		@apply text-base text-[#001526]/90 flex items-center gap-1;
	}

	.slider-percentage {
		@apply text-base text-[#001d33]/70;
	}

	.slider-wrapper {
		@apply w-full relative;
	}

	/* Base slider styling */
	.slider {
		@apply w-full h-2 appearance-none rounded-full;
		outline: none;
		cursor: pointer;
	}

	/* Track styling for modern browsers */
	.slider::-webkit-slider-runnable-track {
		@apply h-2 rounded-full;
	}

	.slider::-moz-range-track {
		@apply h-2 rounded-full;
	}

	.slider::-ms-track {
		@apply h-2 rounded-full;
		border: none;
	}

	.fullWidth {
		@apply w-full;
	}

	/* Thumb styling (circle) */
	.slider::-webkit-slider-thumb {
		@apply w-6 h-6 bg-white rounded-full appearance-none;
		border: 1px solid #a0aebf;
		margin-top: -8px;
	}

	.slider::-moz-range-thumb {
		@apply w-6 h-6 bg-white rounded-full;
		border: 1px solid #a0aebf;
	}

	.slider::-ms-thumb {
		@apply w-6 h-6 bg-white rounded-full;
		border: 1px solid #a0aebf;
		margin-top: 0;
	}
</style>
